import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { IPolicyModel } from '../../interface/IIcafile.interface';
import { resizeOverlayDuration, sessionServiceApi, clientHostname, wasmCgpEnabled,
    receiverVersion, alternateReceiverUrl } from '../../helpers/globalConfig';
import { isReceiverPresent } from '../../services/http/browserRequest';
import { locationSearch, routerCustomerId, routerAppName, decodeBase64 } from '../../helpers/utils'
import { deleteIcaFileTicket } from '../../services/http/browserRequest'
import { startNavigation, endNavigation, registerEventHandlers } from '../../helpers/historyNavigation';
import { ErrorCodeEnum } from '../../enums/errorCode';
import { ProgressStep } from '../../enums/progressStep';
import { CurrentComponentEnum } from '../../enums/currentComponent';
import ProgressComponent from '../progress/progress';
import ErrorComponent from '../error/error';
import TopBanner from './topBanner/topBanner';
import OnboardingDialog from './onboardingDialog/onboardingDialog';

declare var citrix: any;

interface ISessionComponentProps {
    icaFile: string;
    icaFileTicket?: string;
    icaData: string;
    icaPolicies: IPolicyModel;
    singleTry?: boolean,
    initViewport: {
        width: number;
        height: number;
    }
}

const SessionComponent = (props: ISessionComponentProps) => {
    const policies = props.icaPolicies;
    const icaFile = props.icaFile;
    const icaData = props.icaData;
    const icaFileTicket = props?.icaFileTicket;
    let noRetry = !!props?.singleTry;

    let citrixSession: any;

    const [viewportContent, setViewportContent] = useState('width=device-width,initial-scale=1.0');
    const [currentStatus, setCurrentStatus] = useState(CurrentComponentEnum.Progress);
    const [currentSessionStep, setCurrentSessionStep] = useState(ProgressStep.Begin);
    const [errCode, setErrorCode] = useState(ErrorCodeEnum.Default);
    const [errDesc, setErrorDesc] = useState('' as string);
    const [startTime, setStartTime] = useState<number>(0);
    const [waitForResize, setWaitForResize] = useState(false);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    // const [noRetry, setNoRetry] = useState(props?.singleTry || false);
    const [clientLoaded, setClientLoaded] = useState(false);
    const [sessionReady, setSessionReady] = useState(false);

    useEffect(() => {
        findReceiverPath();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [icaFile]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { createSession() }, [clientLoaded]);

    const findReceiverPath = async () => {
        const defaultHost = clientHostname;
        const defaultVersion = receiverVersion;
        const requestedVersion = locationSearch('receiverVersion');

        // If no specific client has been requested, use the default from configuration
        if (!requestedVersion) {
            loadClient(defaultHost, defaultVersion);
            return;
        }

        // Check primary storage for the requestedVersion
        let receiverFound = await isReceiverPresent(defaultHost, requestedVersion);
        if (receiverFound) {
            loadClient(defaultHost, requestedVersion);
            return;
        }

          // Fall back on secondary storage if available
          const alternateHost = alternateReceiverUrl;
          if (alternateHost) {
              receiverFound = await isReceiverPresent(alternateHost, requestedVersion);
              if (receiverFound) {
                  loadClient(alternateHost, requestedVersion);
                  return;
              }
          }
  
          console.log(`Receiver version ${requestedVersion} not found, reverting to default client`); // NOSONAR
          loadClient(defaultHost, defaultVersion);
    }

    const loadClient = (host: string, version: string): void => {
        loadClientUrl(`${window.location.protocol}//${host}/html5client/${version}`);
    }

    const loadClientUrl = (clientPath: string): void => {
        if (clientLoaded || !clientPath) {
            return;
        }
        
        citrix.receiver.setPath(clientPath);
        setClientLoaded(true);

        if (policies) {
            createSession();
        } else {
            setCurrentStatus(CurrentComponentEnum.Error);
        }
    };

    const createSession = (): void => {
        try {
            const connectionParams = {
                launchType: 'embed',
                container: {
                    id: 'client',
                    type: 'iframe'
                },
                bounds: {
                    autoresize: false,
                    width: policies?.mobileViewport ? window.innerWidth : props.initViewport.width,
                    height: policies?.mobileViewport ? window.innerHeight : props.initViewport.height,
                },
                preferences: {
                    ui: {
                        toolbar: {
                            menubar: true,
                            clipboard: Boolean(policies.allowCopy || policies.allowPaste),
                            fileTransfer: Boolean(policies.allowDownload || policies.allowUpload),
                            about: false,
                            lock: false,
                            disconnect: false,
                            logoff: true,
                            fullscreen: true,
                            switchApp: true,
                            preferences: true,
                            viewLogs: false,
                            multiMonitor: false,
                            displayResolution: true
                        },
                        hide: {
                            urlredirection: true,
                            error: false,
                            ftu: true,
                            clipboardOnBoarding: false,
                            copyToDeviceConfirmation: false,
                            highDPIOnBoarding: true
                        },
                        appSwitcher: {
                            showTaskbar: Boolean(policies.nonKiosk)
                        },
                        resizeOverlayDuration: Math.max(0, Math.min(resizeOverlayDuration, 3500))
                    },
                    features: {
                        sessionsharing: false,
                        icaConnectOnAppLaunchFail: false,
                        pdfPrinting: {
                            directPrint: {
                                supportedBrowsers: false
                            }
                        },
                        filetransfer: {
                            allowdownload: false,
                            allowupload: Boolean(policies.allowUpload),
                        },
                    },
                    transport: {
                        wasmCgpEnabled: wasmCgpEnabled
                    },
                    analytics: {
                        enabled: false,
                        connectionEnabled: false
                    }
                }
            };
            citrix.receiver.createSession('', connectionParams, sessionCreated);
        } catch (error) {
            console.log(error); // NOSONAR
        }
    }

    const sessionCreated = (session: any): void => {
        session.addListener('onConnection', onConnection);
        session.addListener('onConnectionClosed', onConnectionClosed);
        session.addListener('onError', onError);
        session.addListener('onResize', onResize);

        citrixSession = session;
        launchSession(session);
    };

    const launchSession = (session: any) => {
        if (!icaFileTicket) {
            if (session && icaFile) {
                if (startTime) {
                    return;
                }

                setStartTime(Date.now());
                let startSessionParams = {
                    type: 'ini',
                    value: icaFile
                }
                if (icaData) {
                    startSessionParams = Object.assign(startSessionParams, { icaStateData: decodeBase64(icaData) })
                }
                session.start(startSessionParams);
            }
        } else {
            const baseUrl = sessionServiceApi.replace('[customer]', encodeURIComponent(routerCustomerId));
            const ticket = icaFileTicket;
            const url = `${baseUrl}/launch/${encodeURIComponent(routerAppName)}/icafileurl/${ticket}`;

            const ifr = document.getElementsByTagName('iframe') as unknown as HTMLIFrameElement;
            ifr.setAttribute('src', url);
            ifr.setAttribute('hidden', 'true');
            document.body.appendChild(ifr);

            setCurrentStatus(CurrentComponentEnum.Error);
            setErrorCode(ErrorCodeEnum.NativeClient);
            return;
        }

        if (session && icaFile) {
            setStartTime(Date.now());
            let stateData = null;
            if (icaData) {
                stateData = decodeBase64(icaData);
                console.log(`Replay data ${stateData.length} bytes`); // NOSONAR
                setWaitForResize(true);
            }        
            setCurrentSessionStep(ProgressStep.SessionStart);
            session.start({
                type: 'ini',
                value: icaFile,
                icaStateData: stateData
            });
        }
    };

    const onConnection = (event: any): void => {
        console.log('onconnection'); // NOSONAR
        let data = event.data;
        switch (data.state) {
            case 'connecting':
                setCurrentSessionStep(ProgressStep.ClientConnecting);
                break;
            case 'connected':
                setCurrentSessionStep(ProgressStep.ClientConnected);
                break;
            case 'sessionReady':
                setSessionReady(true);
                startNavigation();
                if (waitForResize) {
                    citrixSession.changeResolution({ autoresize: true });
                    setTimeout(abandonResize, 1000)
                } else {
                    setTimeout(showClient, 2000)
                }
                break;
            default:
                console.log('Unknown connection event'); // NOSONAR
        }
    };

    const completeConnection = (): void => {
        console.log('completeConnection');  // NOSONAR
        //setSessionCompleted(true); // Doesn't appear to be used.
        setCurrentStatus(CurrentComponentEnum.Session);
        setCurrentSessionStep(ProgressStep.ClientSessionReady);
        noRetry = true;

        deleteIcaFileTicket(routerCustomerId, locationSearch('ticket'));

        registerEventHandlers(onBack, onForward);
    }

    const onConnectionClosed = () => {
        console.info('onConnectionClosed'); // NOSONAR
        endNavigation();
        //If something went wrong, just start over
        if (!noRetry) {
            console.log('launch retry'); // NOSONAR
            noRetry = true;
            const appendCharacter = !window.location.href.includes("?") ? "?" : "&";
            window.location.href = window.location.href + appendCharacter + "singleTry=true";
        }
        setErrorCode(ErrorCodeEnum.ConnectionClosed);
        setCurrentStatus(CurrentComponentEnum.Error);
    };

    const onError = (event: any) => {
        console.log('onError'); // NOSONAR
        endNavigation();
        //If something went wrong, just start over
        if (!noRetry) {
            console.log('launch retry'); // NOSONAR
            noRetry = true;
            const appendCharacter = !window.location.href.includes("?") ? "?" : "&";
            window.location.href = window.location.href + appendCharacter + "singleTry=true";
        }

        setErrorCode(ErrorCodeEnum.Custom);
        setErrorDesc(event?.data?.message);
        setCurrentStatus(CurrentComponentEnum.Error);
    };

    const onResize = (event: any) => {
        console.log('onResize'); // NOSONAR
        const state = event?.data?.state;
        if (state === 'end' && waitForResize) {
            setWaitForResize(false);
            setCurrentStatus(CurrentComponentEnum.Session);
            setCurrentSessionStep(ProgressStep.ClientSessionResized);
        }
    };

    const abandonResize = (): void => {
        if (!waitForResize) {
            return
        }
        console.log('Resize timer exceeded, removing overlay'); // NOSONAR
        setWaitForResize(false);
        showClient();
    }

    const showClient = (): void => {
        noRetry = true;
        setTimeout(completeConnection, 10 * 1000);
        setCurrentSessionStep(ProgressStep.RemoveOverlay);

        if (!policies?.mobileViewport) {
            setViewportContent('');
        }

        citrixSession.changeResolution({ autoresize: true });
    }

    const onBack = (): void => {
        citrixSession.sendSpecialKeys(['alt', 'arrowleft']);
    };

    const onForward = (): void => {
        citrixSession.sendSpecialKeys(['alt', 'arrowright']);
    };

    return (
        <>
            <Helmet>
                <meta name="viewport" content={ viewportContent }/>
            </Helmet>
            { currentStatus === CurrentComponentEnum.Progress && !sessionReady && <ProgressComponent sessionStep={ currentSessionStep }/>}
            { currentStatus === CurrentComponentEnum.Error && <ErrorComponent errorCode={ errCode } errorDesc={ errDesc }/>}
            {
                currentStatus === CurrentComponentEnum.Session && (
                    <div className="launch-component-container">
                        { policies?.remoteBrowserIndicator && <TopBanner animationContainer="session-container"/>}
                        { policies?.nonKiosk && <OnboardingDialog /> }
                    </div>
                )
            }
            <div id="session-container"
                className={`launch-client ${ sessionReady && 'fullscreen'} ${policies?.remoteBrowserIndicator ? 'fullscreen-border' : ''}`}
            >
                <iframe id="client" title="launchwebsite"></iframe>
            </div>
        </>
    )

}

export default SessionComponent;